// Utilities
import * as StringHelper from "@fluentui/utilities";
import { css } from "@fluentui/utilities/lib/css";
import React, { useCallback, useEffect, useRef, useState } from "react";
// Constants
import { useTheme } from "@fluentui/react";
// Components
import { Icon, OverflowButtonType, PersonaSize } from "@fluentui/react";
import { AssignmentPicker } from "../assignmentPicker/AssignmentPicker";
import { Facepile } from "../facepile/Facepile";
// Styles
import { KeyboardUtilities } from "@ms/uno-utilities/lib/local/KeyboardUtilities";
import { assignIconProps } from "../../../icons/icons.styles";
import { getClassNames, getStyles } from "./TaskAssignmentControl.styles";
// Hooks
import { useIsFirstRender } from "../../../hooks/useIsFirstRender";
/**
 * Enumeration of the possible locations the add user icon can be rendered on the task assignment control
 */ export var AddIconPlacement = /*#__PURE__*/ function(AddIconPlacement) {
    /** Icon will be before the assignees in the DOM */ AddIconPlacement[AddIconPlacement["BeforeAssignees"] = 0] = "BeforeAssignees";
    /** Icon will be after the assignees in the DOM */ AddIconPlacement[AddIconPlacement["AfterAssignees"] = 1] = "AfterAssignees";
    return AddIconPlacement;
}({});
/**
 * Enumeration of the possible layout styles for the component
 */ export var LayoutStyle = /*#__PURE__*/ function(LayoutStyle) {
    /** Regular layout */ LayoutStyle[LayoutStyle["Regular"] = 0] = "Regular";
    /** Compact layout that preserves height */ LayoutStyle[LayoutStyle["Compact"] = 1] = "Compact";
    return LayoutStyle;
}({});
/**
 * This component is an assignment control for tasks consisting of a facepile bar that lists current assignments,
 * and when clicked on will open an assignment picker.  The assignment picker will list the current
 * assignees, the pool of assignees, and contain a search bar that will let users filter these and
 * if they have permission search the tenant for more users to add to the assignment pool
 */ export function TaskAssignmentControl(props) {
    const theme = useTheme();
    const { assignees, assignedAgent, availableAgents, onAssignUser, canEditAssignments, getUserPhoto, getUsersWithPlanAccess, getZeroTermQueryResults, onGivePlanAccess, onOpenAssignCallout, onCloseAssignCallout, showPeoplePicker, showAssigneeLimitError, currentUserId, canEditAccessPermissions, canGiveAccessToGuests, canAssignToResourcesWithoutAccess, canAccessPlanViaSharedContainerOnly, maxAssignees, minQueryTermLength, pickerScrollPageSize, onUnassignUser, onUserSearchQueryChanged, getUsersByKeyword, isUsersByKeywordDataReady, onRenderPersona, alertUser, disableTooltips, showAssignToMeButton, hideSingleAssigneeDetails, maxDisplayablePersonas, overflowButtonType, disableFocusZone, addIconPlacement, styles, allowMultiline, layout, preventDismissOnScroll, onAssignCalloutDismiss, onAssignAgent, onUnassignAgent, shouldHideFacepileBar, agentsInfoContentMap } = props;
    const { assignControlLauncherText, readonlyFieldPermissionsTooltip, assignControlAssignToMeButtonText } = props.strings;
    const classNames = getClassNames(getStyles(theme, styles, !canEditAssignments, layout === 1, addIconPlacement === 0, allowMultiline));
    validateProps(props);
    const assignButtonElement = useRef(null);
    const [isPickerOpen, setIsPickerOpen] = useState(showPeoplePicker ?? false);
    const isFirstRender = useIsFirstRender();
    // TODO: PBI #10167300: Refactor assignment picker to remove duplicate rendering logic
    useEffect(()=>{
        // If the component is being rendered for the first time, open the people picker if showPeoplePicker is true
        if (isFirstRender && showPeoplePicker) {
            onOpenAssignCallout?.();
        }
        // Open/close the people picker if the isPickerOpen prop has changed
        if (showPeoplePicker != null && showPeoplePicker !== isPickerOpen) {
            if (showPeoplePicker) {
                onOpenAssignCallout?.();
            } else {
                onCloseAssignCallout?.();
            }
            //internal state changes in case of change in showPeoplePicker
            setIsPickerOpen(()=>{
                return showPeoplePicker ?? false;
            });
        }
    }, [
        showPeoplePicker,
        onOpenAssignCallout,
        onCloseAssignCallout,
        isPickerOpen,
        isFirstRender
    ]);
    const prevIsPickerOpenDep = useRef(isPickerOpen);
    // If the picker state has changed to closed, then call dismissal callbacks
    useEffect(()=>{
        // Do not trap focus for the first render or if the picker open state has not changed
        if (isFirstRender) {
            prevIsPickerOpenDep.current = isPickerOpen;
            return;
        }
        if (prevIsPickerOpenDep.current && !isPickerOpen) {
            if (onAssignCalloutDismiss != null) {
                //Allows the current execution stack to clear before invoking the callback.
                setTimeout(()=>{
                    if (onAssignCalloutDismiss?.()) {
                        assignButtonElement.current?.focus();
                    }
                }, 0);
            } else {
                assignButtonElement.current?.focus();
            }
        }
        prevIsPickerOpenDep.current = isPickerOpen;
    }, [
        isPickerOpen,
        onAssignCalloutDismiss,
        isFirstRender
    ]);
    // /**
    //  * Render the assignment row when the task has no assignments
    //  * TODO: Check if the launcher text makes sense if assignment permissions are not present.
    //  */
    const renderEmptyAssignmentRow = ()=>{
        return /*#__PURE__*/ React.createElement("div", {
            className: classNames.assigneeRow,
            title: !disableTooltips && !canEditAssignments ? readonlyFieldPermissionsTooltip : undefined
        }, canEditAssignments && renderAssignUIButtons(assignControlLauncherText), showAssignToMeButton && renderSelfAssignIcon());
    };
    /**
     * Check if the task has assignees
     */ const hasAssignees = assignees.length > 0 || props.assignedAgent !== null;
    /**
     * Render the assignment row when the task has assignments and when the flag to hide the facepile bar is not set
     */ const renderNonEmptyAssignmentRow = ()=>{
        if (!hasAssignees) {
            return null;
        }
        return /*#__PURE__*/ React.createElement("div", {
            className: classNames.assigneeRow,
            title: !disableTooltips && !canEditAssignments ? readonlyFieldPermissionsTooltip : undefined
        }, addIconPlacement === 0 && canEditAssignments && facepileAssignButton(), renderAssigneePersonas(assignees, props.assignedAgent), addIconPlacement === 1 && canEditAssignments && facepileAssignButton());
    };
    /**
     * Render the personas for assignees
     */ const renderAssigneePersonas = (assignees, assignedAgent)=>{
        let assigneePersonas = assignees.map((assignee)=>{
            return getFacepilePersonaProps(assignee, props);
        });
        const assignedAgentPersona = assignedAgent !== null ? [
            getAgentFacepilePersonaProps(assignedAgent)
        ] : [];
        assigneePersonas = assignedAgentPersona.concat(assigneePersonas); // Agent facepile shown before users
        return /*#__PURE__*/ React.createElement(Facepile, {
            className: css(hideSingleAssigneeDetails && assigneePersonas.length === 1 && "hidePersonaDetails"),
            personas: assigneePersonas,
            maxDisplayablePersonas: maxDisplayablePersonas ?? maxAssignees,
            overflowButtonType: overflowButtonType ?? OverflowButtonType.descriptive,
            overflowButtonProps: {
                onClick: toggleAssignCallout
            },
            onRenderUserPersona: onRenderPersona,
            ariaLabel: generateControlAriaLabel(props),
            personaSize: layout === 0 ? PersonaSize.size32 : PersonaSize.size24
        });
    };
    /**
     * Render the assignment picker
     */ const renderPicker = ()=>{
        if (!isPickerOpen) {
            return null;
        }
        const targetElement = props.targetElement || assignButtonElement;
        if (targetElement == null) {
            return null;
        }
        return /*#__PURE__*/ React.createElement(AssignmentPicker, {
            assignees: assignees,
            assignedAgent: assignedAgent,
            availableAgents: availableAgents,
            zeroTermQueryResults: getZeroTermQueryResults(),
            resourcesWithAccess: getUsersWithPlanAccess(),
            canEditAssignments: canEditAssignments,
            canEditAccessPermissions: canEditAccessPermissions,
            canGiveAccessToGuests: canGiveAccessToGuests,
            canAssignToResourcesWithoutAccess: canAssignToResourcesWithoutAccess,
            canAccessPlanViaSharedContainerOnly: canAccessPlanViaSharedContainerOnly,
            maxAssignees: maxAssignees,
            minQueryTermLength: minQueryTermLength,
            scrollPageSize: pickerScrollPageSize,
            preventDismissOnScroll: preventDismissOnScroll,
            strings: props.strings,
            onAssignUser: onAssignUser,
            onUnassignUser: onUnassignUser,
            onAssignAgent: onAssignAgent,
            onUnassignAgent: onUnassignAgent,
            onGivePlanAccess: onGivePlanAccess,
            onUserSearchQueryChanged: onUserSearchQueryChanged,
            getUsersByKeyword: getUsersByKeyword,
            isUsersByKeywordDataReady: isUsersByKeywordDataReady,
            getUserPhoto: getUserPhoto,
            showAssigneeLimitError: showAssigneeLimitError,
            onAssignCalloutDismiss: onAssignCalloutDismissCallback,
            targetElement: targetElement,
            tabIndex: tabIndex,
            currentUserId: currentUserId,
            onRenderPersona: onRenderPersona,
            alertUser: alertUser,
            agentsInfoContentMap: agentsInfoContentMap
        });
    };
    /**
     * Callback for when the assign callout is dismissed. Call any callbacks if needed before dismissing the PeoplePicker callout
     */ const onAssignCalloutDismissCallback = useCallback(()=>{
        onCloseAssignCallout?.();
        setIsPickerOpen(false);
    }, [
        onCloseAssignCallout
    ]);
    /**
     * Toggle the assign callout open or closed
     */ const toggleAssignCallout = useCallback((evt)=>{
        evt.stopPropagation();
        if (!canEditAssignments) {
            return;
        }
        if (KeyboardUtilities.isButtonPress(evt)) {
            setIsPickerOpen((prevIsPickerOpen)=>{
                if (!prevIsPickerOpen) {
                    onOpenAssignCallout?.();
                } else {
                    onCloseAssignCallout?.();
                }
                return !prevIsPickerOpen;
            });
        }
    }, [
        canEditAssignments,
        onOpenAssignCallout,
        onCloseAssignCallout
    ]);
    /**
     * Callback to render the self-assign icon
     */ const renderSelfAssignIcon = ()=>{
        let isCurrentUserAssigned = false;
        for (const user of assignees){
            // Checking if the current user is assigned to current task.
            if (user.id === currentUserId) {
                isCurrentUserAssigned = true;
                break;
            }
        }
        if (!isCurrentUserAssigned) {
            return /*#__PURE__*/ React.createElement("button", {
                className: classNames.selfAssign,
                onClick: ()=>selfAssignFunctionality()
            }, renderAssignUIButtons(assignControlAssignToMeButtonText));
        }
        return null;
    };
    /**
     * Self-Assign button functionality
     */ const selfAssignFunctionality = ()=>{
        onAssignUser(currentUserId);
        setIsPickerOpen(()=>{
            return showPeoplePicker ?? true;
        });
    };
    /**
     * Helper method to render assign and self-assign buttons
     * @param buttonLabelName the label for the button to be rendered
     */ const renderAssignUIButtons = (buttonLabelName)=>{
        return /*#__PURE__*/ React.createElement("div", {
            className: classNames.assignDiv
        }, /*#__PURE__*/ React.createElement(Icon, {
            ...assignIconProps,
            className: css(assignIconProps.className, classNames.addFriendIcon)
        }), /*#__PURE__*/ React.createElement("span", {
            className: classNames.assignLabel,
            "aria-label": buttonLabelName
        }, buttonLabelName));
    };
    /**
     * Helper method to render facepile assign button
     */ const facepileAssignButton = ()=>{
        return /*#__PURE__*/ React.createElement("div", {
            className: "ms-Facepile-addButton ms-Facepile-itemButton"
        }, /*#__PURE__*/ React.createElement(Icon, assignIconProps));
    };
    /**
     * Render the component
     */ const assigneeRow = !hasAssignees ? renderEmptyAssignmentRow() : renderNonEmptyAssignmentRow();
    const className = css(classNames.root, props.className);
    // In order to make the control not tabbable, role must not be set to
    // button. When role="button", tabIndex is set to 0 regardless of what
    // this code does.
    const tabIndex = props.tabIndex != null ? props.tabIndex : 0;
    // When the control is explicitly set as not tabbable, role should not be "button"
    // Otherwise it should be defined as "button" to avoid narrator announcement issue in Edge
    const role = props.tabIndex && props.tabIndex < 0 ? undefined : "button";
    // The assignments button has been changed from a button to a div to allow for drag start on IE, Edge, and Firefox. When this is fixed in ReactDND, change back to button VSO 1700377
    return /*#__PURE__*/ React.createElement("div", {
        className: className,
        ref: assignButtonElement,
        onClick: toggleAssignCallout,
        onKeyUp: toggleAssignCallout,
        role: role,
        tabIndex: tabIndex,
        "aria-label": generateControlAriaLabel(props),
        title: !disableTooltips ? assignControlLauncherText : undefined,
        onKeyPress: disableFocusZone,
        "data-is-focusable": true
    }, !shouldHideFacepileBar && assigneeRow, renderPicker());
}
/**
 * Validate the props passed to the component
 * @param props Props passed in
 */ function validateProps(props) {
    if (props.maxAssignees != null && props.maxAssignees < 0) {
        throw new Error("InvalidArgumentException: maxAssignees");
    }
}
/**
 * Generate the aria label for the control. Examples:
 * "Assigned to User1, Open to assign users to this task"
 * "Assigned to User1, User2, User3, Open to assign users to this task"
 * "Not assigned, Open to assign users to this task"
 */ function generateControlAriaLabel(props) {
    const { assignees, canEditAssignments, assignedAgent } = props;
    const { assignControlAriaLabelPrefix, assignControlAriaLabelPrefixNoAssignees, assignControlAriaLabelSuffix, assignControlListSeparator, assignControlAriaLabelConstruction } = props.strings;
    let ariaLabelAssignmentsPrefix;
    let ariaLabelAssignmentsList = "";
    const suffix = canEditAssignments ? assignControlAriaLabelSuffix : "";
    if (assignees.length > 0 || assignedAgent !== null) {
        ariaLabelAssignmentsPrefix = assignControlAriaLabelPrefix;
        if (assignedAgent !== null) {
            ariaLabelAssignmentsList += assignedAgent.displayName;
            ariaLabelAssignmentsList += assignControlListSeparator;
        }
        for (const user of assignees){
            ariaLabelAssignmentsList += user.displayName;
            ariaLabelAssignmentsList += assignControlListSeparator;
        }
    } else {
        ariaLabelAssignmentsPrefix = assignControlAriaLabelPrefixNoAssignees;
    }
    return StringHelper.format(assignControlAriaLabelConstruction, ariaLabelAssignmentsPrefix, ariaLabelAssignmentsList, suffix);
}
/**
 * Get facepile props for users to be rendered on the bar that launches the assignment callout
 * @param user To get facepile props for
 */ function getFacepilePersonaProps(user, props) {
    const userPhoto = props.getUserPhoto(user.id);
    return {
        personaName: user.formattedDisplayName,
        imageUrl: userPhoto ? userPhoto : undefined,
        data: {
            user: user
        }
    };
}
/**
 * @returns facepile props for agents to be rendered on the bar that launches the assignment callout
 * @param agent To get facepile props for
 */ function getAgentFacepilePersonaProps(agent) {
    return {
        personaName: agent.displayName,
        imageUrl: agent.logoUrl,
        data: {
            agent: agent
        }
    };
}
